import { pgRpc } from './postgrest';

interface XY {
  x: number;
  y: number;
}

interface GeocodeResponse {
  results: Array<{
    geometry: {
      lat: number;
      lng: number;
    };
  }>;
}

export class CoordinateService {
  async getCenterOfSection(
    section: string,
    township: string,
    range: string,
    meridian: string,
  ): Promise<{ longitude: number; latitude: number }> {
    try {
      // RETURNS TABLE(center_point, x, y) -> PostgREST returns an array of rows.
      const rows = await pgRpc<Array<{ x: number; y: number }>>('get_center_section', {
        v_sect: section,
        v_ptwp: township,
        v_prge: range,
        v_pmer: meridian,
      });
      const data = rows[0];

      if (data?.x && data?.y) {
        return {
          longitude: data.x,
          latitude: data.y,
        };
      }
      throw new Error('Section not found');
    } catch (error) {
      console.error('Error getting section center:', error);
      throw error;
    }
  }

  async getCoordinateByRm(rmno: string): Promise<{ longitude: number; latitude: number }> {
    try {
      const rows = await pgRpc<XY[]>('get_latlng_rm', { v_rmno: rmno });
      const data = rows[0];

      if (data?.x && data?.y) {
        return {
          longitude: data.x,
          latitude: data.y,
        };
      }
      throw new Error('No coordinates found for RM');
    } catch (error) {
      console.error('Error getting RM coordinates:', error);
      throw error;
    }
  }

  async getCoordinateByPolygonId(
    polygonId: string,
  ): Promise<{ longitude: number; latitude: number }> {
    try {
      const rows = await pgRpc<XY[]>('get_latlng_poly_id', { v_poly_id: polygonId });
      const data = rows[0];

      if (data?.x && data?.y) {
        return {
          longitude: data.x,
          latitude: data.y,
        };
      }
      throw new Error('Polygon not found');
    } catch (error) {
      console.error('Error getting polygon coordinates:', error);
      throw error;
    }
  }

  async getPolygonFeatureInfoForUrl(url: string): Promise<any> {
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      console.error('Error fetching polygon feature info:', error);
      throw error;
    }
  }

  async geocodeCity(cityName: string): Promise<{ longitude: number; latitude: number }> {
    try {
      const apiKey = 'c4ac603702ae419597108bb597a280fd';
      const url = `https://api.opencagedata.com/geocode/v1/json?q=${encodeURIComponent(cityName)}&key=${apiKey}&language=en&pretty=1&countrycode=ca`;

      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to geocode city');

      const data: GeocodeResponse = await response.json();

      if (data.results && data.results.length > 0) {
        const geometry = data.results[0].geometry;
        return {
          longitude: geometry.lng,
          latitude: geometry.lat,
        };
      }
      throw new Error('No coordinates found for city');
    } catch (error) {
      console.error('Error geocoding city:', error);
      throw error;
    }
  }

  async checkCoordinate(latitude: number, longitude: number, radius: number = 100): Promise<any> {
    try {
      // v_longitude/v_latitude are text params; v_radius is bigint.
      const rows = await pgRpc<Array<{ pointlayer: boolean; polygonlayer: boolean }>>(
        'check_coordinate',
        {
          v_longitude: String(longitude),
          v_latitude: String(latitude),
          v_radius: radius,
        },
      );
      return rows[0] ?? { pointlayer: false, polygonlayer: false };
    } catch (error) {
      console.error('Error checking coordinates:', error);
      throw error;
    }
  }

  async getEcoRegion(latitude: number, longitude: number): Promise<any> {
    try {
      const rows = await pgRpc<Array<{ ecoregion: string }>>('get_ecoregions_range_mod', {
        v_latitude: String(latitude),
        v_longitude: String(longitude),
      });
      return rows[0] ?? null;
    } catch (error) {
      console.error('Error getting eco region:', error);
      throw error;
    }
  }

  async getSurficialGeology(latitude: number, longitude: number): Promise<any> {
    try {
      const rows = await pgRpc<Array<{ code: string; main_envir: string }>>(
        'get_surficial_geology',
        {
          v_longitude: String(longitude),
          v_latitude: String(latitude),
        },
      );
      return rows[0] ?? null;
    } catch (error) {
      console.error('Error getting surficial geology:', error);
      throw error;
    }
  }
}

export const coordinateService = new CoordinateService();
